import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import Constants from '../../utils/constants';
import { authHeader } from '../../utils/headers';

// Derive API base from a full URL constant
function getApiBase() {
  try {
    const u = new URL(Constants.API_ROUTE_GRAFFITI_RUES_SEARCH.replace(':clientId', '0'));
    return `${u.protocol}//${u.host}`;
  } catch (e) {
    // fallback to env base
    return import.meta.env.VITE_APIURL || '';
  }
}

export const graffitiApi = createApi({
  reducerPath: 'graffitiApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBase(),
    prepareHeaders: (headers) => {
      const token = authHeader();
      if (typeof token === 'string' && token) {
        headers.set('authorization', token);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    searchRues: builder.query({
      query: ({ clientId, q }) => {
        const base = Constants.API_ROUTE_GRAFFITI_RUES_SEARCH.replace(':clientId', clientId);
        const params = new URLSearchParams();
        if (q) params.set('q', q);
        const qs = params.toString();
        return qs ? `${base}?${qs}` : base;
      },
      // Empty array on short terms as per backend
      transformResponse: (response) => Array.isArray(response) ? response : [],
    }),
    getInterventions: builder.query({
      // params: { patrimoineId: number, dateDebut?: 'YYYY-MM-DD', dateFin?: 'YYYY-MM-DD', communeIds?: number[], quartierIds?: number[], rueIds?: number[], period?: legacy }
      query: ({ patrimoineId, dateDebut, dateFin, communeIds, quartierIds, rueIds, period }) => {
        const base = Constants.API_ROUTE_GRAFFITI_INTERVENTIONS;
        const params = new URLSearchParams();
        if (patrimoineId != null) params.set('patrimoineId', String(patrimoineId));
        // Prefer explicit date bounds if provided
        if (dateDebut) params.set('dateDebut', String(dateDebut));
        if (dateFin) params.set('dateFin', String(dateFin));
        // Backward compatibility: keep period if dates are not provided
        if (!dateDebut && !dateFin && period != null) params.set('period', String(period));
        const addMulti = (key, arr) => {
          if (!arr || !Array.isArray(arr) || arr.length === 0) return;
          // backend accepts repeated keys or CSV; we'll send repeated keys for clarity
          arr.forEach((id) => {
            const n = Number(id);
            if (Number.isFinite(n) && n > 0) params.append(key, String(n));
          });
        };
        addMulti('communeId', communeIds);
        addMulti('quartierId', quartierIds);
        addMulti('rueId', rueIds);
        const qs = params.toString();
        return qs ? `${base}?${qs}` : base;
      },
      // Expect an array; normalize otherwise
      transformResponse: (response) => Array.isArray(response) ? response : [],
    }),
    // Détail d'une intervention graffiti par ID
    getInterventionDetail: builder.query({
      query: ({ id }) => {
        const base = Constants.API_ROUTE_GRAFFITI_INTERVENTION_DETAIL.replace(':id', id);
        return base;
      },
      // Retourne un objet; renvoie {} par défaut
      transformResponse: (response) => (response && typeof response === 'object') ? response : {},
    }),
    exportGraffiti: builder.mutation({
      // Same params as getInterventions; adds optional format ('json' for debug)
      query: ({ patrimoineId, dateDebut, dateFin, communeIds, quartierIds, rueIds, period, format }) => {
        const base = Constants.API_ROUTE_GRAFFITI_EXPORT;
        const params = new URLSearchParams();
        if (patrimoineId != null) params.set('patrimoineId', String(patrimoineId));
        if (dateDebut) params.set('dateDebut', String(dateDebut));
        if (dateFin) params.set('dateFin', String(dateFin));
        if (!dateDebut && !dateFin && period != null) params.set('period', String(period));
        const addMulti = (key, arr) => {
          if (!arr || !Array.isArray(arr) || arr.length === 0) return;
          arr.forEach((id) => {
            const n = Number(id);
            if (Number.isFinite(n) && n > 0) params.append(key, String(n));
          });
        };
        addMulti('communeId', communeIds);
        addMulti('quartierId', quartierIds);
        addMulti('rueId', rueIds);
        if (format) params.set('format', String(format));
        const qs = params.toString();
        return {
          url: qs ? `${base}?${qs}` : base,
          method: 'GET',
          // Let the browser handle content-type; expect a blob
          responseHandler: async (response) => {
            // If JSON debug requested or server returns JSON (e.g., error), pass it through
            const ct = response.headers.get('content-type') || '';
            if (ct.includes('application/json')) {
              return response.json();
            }
            return response.blob();
          },
        };
      },
    }),
  }),
});

export const { useLazySearchRuesQuery, useGetInterventionsQuery, useGetInterventionDetailQuery, useExportGraffitiMutation } = graffitiApi;
